import { MATERIAL, steps } from '../../Constants/Steps';
import * as apiActions from '@configuratorware/configurator-frontendgui/App/Reducers/Actions';
import { difference, get } from 'lodash';
import { setCalculationEnabled } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { ALLSEATS } from '../../Constants/SeatPositions';
import { isAllSeatsSelected } from './Selectors';

export const actions = {
  SET_SELECTED_STAGE: 'SET_SELECTED_STAGE',
  SET_TARGET_STAGE: 'SET_TARGET_STAGE',
  STEP_TO_NEXT_STAGE: 'STEP_TO_NEXT_STAGE',
  SWITCH_SELECTION: 'SWITCH_SELECTION',
  SET_SELECTED_ATTRIBUTE_VALUE: 'SET_SELECTED_ATTRIBUTE_VALUE',
  SET_SELECTED_ATTRIBUTE: 'SET_SELECTED_ATTRIBUTE',
  SET_FINDER_DATA: 'SET_FINDER_DATA',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_NEXT_QUESTION: 'SET_NEXT_QUESTION',
  SET_SELECTED_ANSWER: 'SET_SELECTED_ANSWER',
  SET_SHOW_PRODUCT_PARTS: 'SET_SHOW_PRODUCT_PARTS',
  SET_SELECTED_DESIGN: 'SET_SELECTED_DESIGN',
  SAVE_SEAT_DATA: 'SAVE_SEAT_DATA',
  SAVE_SEAT_TYPE_DATA: 'SAVE_SEAT_TYPE_DATA',
  SET_SHOW_DESIGN_VIEW: 'SET_SHOW_DESIGN_VIEW',
  SET_SEAT_POSITIONS_DATA: 'SET_SEAT_POSITIONS_DATA',
  VALIDATE_SEAT_POSITIONS_DATA: 'VALIDATE_SEAT_POSITIONS_DATA',
  RESET_ADDITIONAL_POSITIONS_LOADED: 'RESET_ADDITIONAL_POSITIONS_LOADED',
  SET_QUESTIONS: 'SET_QUESTIONS',
};

const setCalculationForStage = (stage) => async (dispatch, getState) => {
  const { configurator } = getState();
  if (stage === MATERIAL && configurator.configuration.item.identifier) {
    dispatch(setCalculationEnabled(true));
  } else {
    dispatch(setCalculationEnabled(false));
  }
};

const updateAdditionalDataIfNeeded = (stage) => (dispatch, getState) => {
  const { configurator } = getState();
  if (stage === MATERIAL && configurator.configuration.item.identifier) {
    return dispatch(fetchAdditionalData());
  }
};

export const setSelectedStage = (stage) => async (dispatch) => {
  dispatch({
    type: actions.SET_SELECTED_STAGE,
    stage,
  });
  dispatch(setCalculationForStage(stage));
  await dispatch(updateAdditionalDataIfNeeded(stage));
};

export const setSelectedDesign = (design) => (dispatch) => {
  dispatch({
    type: actions.SET_SELECTED_DESIGN,
    design,
  });
};

export const stepToNextStage = () => (dispatch, getState) => {
  const state = getState().configurator;
  const stageIndex = steps.indexOf(state.selectedStage);
  const lastStage = steps.length - 1;
  const nextStage = stageIndex < lastStage ? stageIndex + 1 : lastStage;
  dispatch(setSelectedStage(steps[nextStage]));
};

export const setIsLoading = (flag) => async (dispatch) => {
  dispatch({
    type: actions.SET_IS_LOADING,
    flag,
  });
};

export const setShowProductParts = (flag) => (dispatch) => {
  dispatch({
    type: actions.SET_SHOW_PRODUCT_PARTS,
    flag,
  });
};

export const setNextQuestion = (data) => async (dispatch, getState) => {
  data.identifier &&
    (await dispatch({
      type: actions.SET_NEXT_QUESTION,
      data,
    }));

  // handle seat_position question with a single answer separately here (RAUI-197)
  if (data.identifier === 'seat_position' && data.answers.length === 1) {
    const selectedAnswer = data.answers[0];
    dispatch(saveSeatData({ ...data, selectedAnswer }));
    await dispatch({
      type: actions.SET_SELECTED_ANSWER,
      identifier: data.identifier,
      answer: selectedAnswer,
    });
    dispatch(callFinder());
    return;
  }

  const { configurator } = getState();
  const {
    questions,
    configuration: { customdata },
  } = configurator;
  const seatPosition = get(customdata, 'seatData', []).find((s) => s.identifier === 'seat_position');
  // based on seat_position questions will be iterated for all seats based on seat configuration saved in customdata
  const seatPositionQuestion = questions.find((q) => q.identifier === 'seat_position');
  if (seatPosition && seatPosition.selectedAnswer === ALLSEATS) {
    if (
      data.identifier !== 'design' &&
      data.answers.length === 1 &&
      !((data.identifier === 'arm_rest_available' || data.identifier === 'kst_available') && data.answers[0])
    ) {
      await dispatch(
        saveSeatTypeData(seatPositionQuestion.selectedAnswer, { ...data, selectedAnswer: data.answers[0] })
      );
      return dispatch(selectAnswer(data.identifier, data.answers[0]));
    } else {
      // save witout selectedAnswer too, inorder to save question in customData if currentSeatType is changed
      dispatch(saveSeatTypeData(seatPositionQuestion.selectedAnswer, data));
    }
    if (data.identifier === 'design') {
      const availableSeatTypes = get(seatPositionQuestion, 'answers', []).filter((ans) => ans !== ALLSEATS);

      dispatch(saveSeatTypeData(seatPositionQuestion.selectedAnswer, { ...data, selectedAnswer: null }));

      const seatData = get(getState().configurator.configuration.customdata, 'seatData', []);

      const unfinishedSeatType = seatData.find(
        ({ questions }) => questions && !questions.find(({ identifier }) => identifier === 'design')
      );

      if (unfinishedSeatType) {
        return dispatch(
          selectAnswer('seat_position', unfinishedSeatType.identifier, { doFinderCall: false })
        );
      }

      const customDataSeatTypes = seatData
        .filter((s) => s.identifier !== 'seat_position')
        .map((a) => a.identifier);
      const remainingSeatTypes = difference(availableSeatTypes, customDataSeatTypes);
      if (remainingSeatTypes && remainingSeatTypes.length > 0) {
        return dispatch(selectAnswer('seat_position', remainingSeatTypes[0]));
      } else {
        return dispatch(setShowDesignView(true));
      }
    }
  } else if (seatPosition && seatPosition.selectedAnswer) {
    await dispatch(
      saveSeatTypeData(
        seatPositionQuestion.selectedAnswer,
        data.answers.length === 1 && data.answers[0] ? { ...data, selectedAnswer: data.answers[0] } : data
      )
    );
  }

  if (
    data.identifier !== 'design' &&
    data.answers.length === 1 &&
    !(
      (data.identifier === 'arm_rest_available' ||
        data.identifier === 'kst_available' ||
        data.identifier === 'year_start') &&
      data.answers[0]
    )
  ) {
    await dispatch({
      type: actions.SET_SELECTED_ANSWER,
      identifier: data.identifier,
      answer: data.answers[0],
    });
    dispatch(callFinder());
  }
  if (data.type === 'input' && !data.required) {
    setTimeout(() => dispatch(callFinder()), 400);
  }
};

export const selectAnswer =
  (identifier, answer, options = {}) =>
  async (dispatch) => {
    await dispatch({
      type: actions.SET_SELECTED_ANSWER,
      identifier,
      answer,
    });
    const { doFinderCall = true } = options;
    if (doFinderCall && answer !== 'default' && identifier !== 'designer') {
      dispatch(callFinder());
    }
  };

export const callFinder = () => (dispatch, getState) => {
  const { configurator } = getState();
  const { questions } = configurator;
  const allSeatsSelected = isAllSeatsSelected(configurator.configuration);
  dispatch(
    apiActions.postDataAsync(
      '/finder/processquestions',
      { allSeatsSelected, questions: questions },
      setNextQuestion
    )
  );
};

export const saveSeatData = (data) => (dispatch) => {
  dispatch({
    type: actions.SAVE_SEAT_DATA,
    data,
  });
};

export const setShowDesignView = (flag) => (dispatch) => {
  dispatch({
    type: actions.SET_SHOW_DESIGN_VIEW,
    flag,
  });
};

export const setQuestions = (questions) => (dispatch) => {
  dispatch({
    type: actions.SET_QUESTIONS,
    questions,
  });
};

export const saveSeatTypeData = (seatType, question) => (dispatch) => {
  dispatch({
    type: actions.SAVE_SEAT_TYPE_DATA,
    seatType: question.seatType ? question.seatType : seatType,
    question,
  });
};

export const isSeatPositionDataValid = (state) => {
  const seatPositions = get(
    state.configurator.configuration.customdata,
    'seatPositionsData.seatPositions',
    []
  );
  return !seatPositions.some((seatPosition) =>
    seatPosition.featureSelection.some((feature) => feature.error)
  );
};

export const setSeatPositionsData = (data) => async (dispatch, getState) => {
  await dispatch({
    type: actions.SET_SEAT_POSITIONS_DATA,
    data,
  });
  if (!isSeatPositionDataValid(getState())) {
    dispatch(validateSeatPositionData());
  }
};

export const validateSeatPositionData = () => (dispatch, getState) => {
  dispatch({
    type: actions.VALIDATE_SEAT_POSITIONS_DATA,
  });
  return isSeatPositionDataValid(getState());
};

export const fetchAdditionalData = () => (dispatch, getState) => {
  const { configuration } = getState().configurator;
  dispatch({
    type: actions.RESET_ADDITIONAL_POSITIONS_LOADED,
  });
  return dispatch(
    apiActions.postDataAsync('/configuration/additionalpositions', { configuration }, setSeatPositionsData)
  );
};
